export type NavigationItem = {
  key: string;
  label: string;
  capabilities?: string[];
  platformAdminOnly?: boolean;
  children?: NavigationItem[];
};

// The sidebar describes destinations, not permissions. Route guards and APIs remain authoritative.
export const navigationItems: NavigationItem[] = [
  { key: '/', label: '工作台' },
  { key: 'business', label: '服务运营', children: [
    { key: '/work-orders', label: '服务单管理' },
    { key: '/task-annotation', label: '任务标定', capabilities: ['TASK_ANNOTATE'] },
    { key: '/dispatch-center', label: '派单中心', capabilities: ['DISPATCH_MANAGE'] },
    { key: '/task-reviews', label: '任务与完工核准', capabilities: ['TASK_REVIEW'] },
    { key: '/evaluations', label: '评价中心', capabilities: ['EVALUATION_MANAGE', 'SALES_EVALUATE', 'EXTERNAL_SHARE_MANAGE'] }
  ] },
  { key: 'fulfillment', label: '服务执行', children: [
    { key: '/visits', label: '上门服务', capabilities: ['ONSITE_MANAGE', 'WORK_ORDER_EXECUTE'] },
    { key: '/logistics', label: '中心维修与物流', capabilities: ['LOGISTICS_MANAGE', 'CENTER_REPAIR_MANAGE'] }
  ] },
  { key: '/dashboard', label: '数据驾驶舱', capabilities: ['DASHBOARD_VIEW_COMPANY', 'DASHBOARD_VIEW_ORGANIZATION', 'DASHBOARD_VIEW_SELF'] },
  { key: 'bonus', label: '奖金管理', children: [
    { key: '/bonus/rules', label: '月度奖金规则', capabilities: ['BONUS_MANAGE'] },
    { key: '/bonus/preview', label: '月度奖金试算', capabilities: ['BONUS_SELF_VIEW'] },
    { key: '/bonus/settlement', label: '月度奖金结算', capabilities: ['BONUS_SELF_VIEW'] }
  ] },
  { key: 'rules', label: '规则与资源', children: [
    { key: '/settings/dispatch-roles', label: '派单资格', capabilities: ['DISPATCH_MANAGE'] },
    { key: '/settings/task-labels', label: '服务任务目录', capabilities: ['TASK_CATALOG_MANAGE'] },
    { key: '/settings/service-centers', label: '维修中心', capabilities: ['SERVICE_CENTER_MANAGE'] },
    { key: '/settings/onsite-coverages', label: '上门服务范围', capabilities: ['ONSITE_MANAGE'] }
  ] },
  { key: 'system', label: '系统管理', children: [
    { key: '/settings/accounts', label: '账号与权限', capabilities: ['ACCESS_MANAGE'] },
    { key: '/settings/master-data', label: '客户与设备', capabilities: ['MASTER_DATA_MANAGE'] },
    { key: '/settings/map-provider', label: '地图服务配置', platformAdminOnly: true }
  ] }
];

export function visibleNavigation(can: (capability: string) => boolean, isPlatformAdmin: boolean): NavigationItem[] {
  const permitted = (item: NavigationItem) =>
    (!item.platformAdminOnly || isPlatformAdmin) && (!item.capabilities || item.capabilities.some(can));
  return navigationItems.flatMap((item) => {
    if (!item.children) return permitted(item) ? [item] : [];
    const children = item.children.filter(permitted);
    return children.length ? [{ ...item, children }] : [];
  });
}

export function selectedNavigation(pathname: string, items: NavigationItem[]): { key: string; group: string | undefined } {
  const leaves = items.flatMap((item) => item.children ?? [item]);
  const known = (key: string) => leaves.some((item) => item.key === key);
  const alias = pathname === '/dispatch-pool'
    ? ['/dispatch-center', '/task-annotation'].find(known)
    : pathname === '/settings/access'
      ? ['/settings/accounts', '/settings/master-data'].find(known)
      : pathname === '/map/pick' && known('/settings/onsite-coverages')
        ? '/settings/onsite-coverages'
        : undefined;
  const matching = alias ?? leaves.filter((item) => item.key !== '/' && (pathname === item.key || pathname.startsWith(`${item.key}/`)))
    .sort((left, right) => right.key.length - left.key.length)[0]?.key ?? '/';
  return { key: matching, group: items.find((item) => item.children?.some((child) => child.key === matching))?.key };
}
